import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Cloud, Send, Sparkles, ShieldCheck, CheckCircle2, Circle, Loader2,
  RotateCcw, Clock, Monitor, ArrowLeft, Zap, Layers, Cpu, LogOut, FileText,
  Paperclip, Users, Plus, X, UserPlus, Settings as SettingsIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { SAMPLE_TASKS, LLM_MODELS } from "../mock";
import { useAuth } from "../context/AuthContext";
import LegalFooterLinks from "../legal/LegalFooterLinks";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import { myInvites, respondInvite, teamInvites, revokeInvite, errText, PENDING_INVITE_KEY } from "../lib/teamInvites";
import HelpButton from "@/components/HelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSid = () => {
  let s = localStorage.getItem("nimbus_sid");
  if (!s) {
    const randomId = globalThis.crypto && globalThis.crypto.randomUUID;
    s = (randomId && randomId.call(globalThis.crypto)) || `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("nimbus_sid", s);
  }
  return s;
};

// Very light markdown -> lines renderer (bold + bullets)
const renderOutput = (text) =>
  String(text || "").split("\n").map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={j} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );
    return <p key={i} className={line.trim().startsWith("-") ? "pl-1" : ""}>{parts.length ? parts : "\u00a0"}</p>;
  });

const Workspace = () => {
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sid = useRef(getSid());
  const [task, setTask] = useState("");
  const [mode, setMode] = useState("complex");
  const [modelId, setModelId] = useState("gpt-5.4");
  const [running, setRunning] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scope, setScope] = useState("personal"); // 'personal' | team_id
  const [attachments, setAttachments] = useState([]); // [{id, filename}] up to 5
  const [skills, setSkills] = useState([]);
  const [skillId, setSkillId] = useState("none");
  const [uploading, setUploading] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [teamBusy, setTeamBusy] = useState(false);
  const [myInv, setMyInv] = useState([]);
  const [teamPending, setTeamPending] = useState([]);
  const fileRef = useRef(null);
  const timers = useRef([]);
  const logRef = useRef(null);

  const teamId = scope === "personal" ? null : scope;
  // Team plan: every run uses one fixed model (GET /api/plan run_model), so the picker is hidden.
  const [fixedModel, setFixedModel] = useState(null);
  useEffect(() => {
    if (!user) { setFixedModel(null); return; }
    axios.get(`${API}/plan`, { withCredentials: true }).then((r) => setFixedModel(r.data.run_model || null)).catch(() => {});
  }, [user]);

  const modelMeta = (() => {
    for (const g of LLM_MODELS) {
      const m = g.models.find((x) => x.id === modelId);
      if (m) return { provider: g.provider, name: m.name, runWeightComplex: m.runWeightComplex || 1 };
    }
    return { provider: "openai", name: modelId, runWeightComplex: 1 };
  })();
  const runWeight = fixedModel ? 1 : (mode === "complex" ? modelMeta.runWeightComplex : 1);

  const fetchTeams = async () => {
    if (!user) { setTeams([]); return; }
    try {
      const res = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(res.data || []);
    } catch {
      setTeams([]);
    }
  };

  const fetchJobs = async () => {
    if (!user) {
      setJobs([]);
      return;
    }
    try {
      const params = teamId ? { limit: 8, team_id: teamId } : { limit: 8 };
      const res = await axios.get(`${API}/runs`, { params, withCredentials: true });
      setJobs(res.data || []);
    } catch {
      setJobs([]);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    setTeamBusy(true);
    try {
      const res = await axios.post(`${API}/teams`, { name: newTeamName.trim() }, { withCredentials: true });
      setNewTeamName("");
      await fetchTeams();
      setScope(res.data.id);
      toast({ title: "Team created", description: `"${res.data.name}" is ready.` });
    } catch {
      toast({ title: "Could not create team", description: "Please try again." });
    } finally {
      setTeamBusy(false);
    }
  };

  const fetchInvites = async () => {
    if (!user) { setMyInv([]); return; }
    try { setMyInv((await myInvites()).invites || []); } catch { setMyInv([]); }
  };

  const fetchTeamPending = async () => {
    if (!user || !teamId) { setTeamPending([]); return; }
    try { setTeamPending(await teamInvites(teamId)); } catch { setTeamPending([]); }
  };

  const answerInvite = async (inv, accept) => {
    try {
      const r = await respondInvite(inv.id, accept);
      await Promise.all([fetchInvites(), fetchTeams()]);
      if (accept) setScope(r.team_id);
      toast({ title: accept ? "Joined team" : "Invitation declined", description: accept ? `You're now in ${r.team_name}.` : `${inv.team_name} won't be shared with you.` });
    } catch (e) {
      toast({ title: "Couldn't update the invitation", description: errText(e, "Please try again.") });
    }
  };

  const revoke = async (inv) => {
    try {
      await revokeInvite(teamId, inv.id);
      await fetchTeamPending();
      toast({ title: "Invitation withdrawn", description: `The link sent to ${inv.email} no longer works.` });
    } catch (e) {
      toast({ title: "Couldn't withdraw it", description: errText(e, "Please try again.") });
    }
  };

  const inviteMember = async () => {
    if (!teamId || !inviteEmail.trim()) return;
    setTeamBusy(true);
    try {
      const res = await axios.post(`${API}/teams/${teamId}/invite`, { email: inviteEmail.trim() }, { withCredentials: true });
      setInviteEmail("");
      await fetchTeamPending();
      // Same reply whether or not the address has an account: they join only if they accept.
      toast({ title: "Invitation sent", description: res.data.message || `${res.data.email} can accept from the email or in the app.` });
    } catch (e) {
      toast({ title: "Invite failed", description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setTeamBusy(false);
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { navigate("/login"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      if (teamId) form.append("team_id", teamId);
      const res = await axios.post(`${API}/uploads`, form, { withCredentials: true });
      setAttachments((prev) => [...prev, { id: res.data.id, filename: res.data.filename }].slice(0, 5));
      toast({ title: "File attached", description: res.data.filename });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Upload failed", description: typeof detail === "string" ? detail : "Check file type/size." });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const fetchSkills = async () => {
    if (!user) { setSkills([]); return; }
    try {
      const res = await axios.get(`${API}/skills`, { params: teamId ? { team_id: teamId } : {}, withCredentials: true });
      setSkills(res.data.skills || []);
    } catch { setSkills([]); }
  };

  const saveSkill = async () => {
    if (!task.trim()) { toast({ title: "Write the instructions first", description: "The task text becomes the skill." }); return; }
    const name = window.prompt("Name this skill", task.trim().slice(0, 40));
    if (!name) return;
    try {
      const res = await axios.post(`${API}/skills`, { name, instructions: task.trim(), mode, team_id: teamId }, { withCredentials: true });
      await fetchSkills();
      setSkillId(res.data.id);
      toast({ title: "Skill saved", description: `Pick "${name}" to reuse these instructions.` });
    } catch (e) {
      toast({ title: "Could not save skill", description: e?.response?.data?.detail || "Please try again." });
    }
  };

  useEffect(() => {
    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, scope]);

  useEffect(() => {
    fetchTeams();
    fetchInvites();
    // Came back from sign-in after opening an emailed invite link: finish it there.
    if (user) {
      let pending = null;
      try { pending = sessionStorage.getItem(PENDING_INVITE_KEY); sessionStorage.removeItem(PENDING_INVITE_KEY); } catch {}
      if (pending) navigate(`/invite?token=${encodeURIComponent(pending)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchTeamPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, scope]);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, scope]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [activeStep]);

  const startRun = () => {
    if (!user) {
      toast({ title: "Sign in to run the agent", description: "Runs are tied to your account." });
      navigate("/login");
      return;
    }
    if (!task.trim()) {
      toast({ title: "Describe a task first", description: "Type a goal or pick a sample below." });
      return;
    }
    setRunning(true);
    setResult(null);
    setActiveStep(0);

    axios
      .post(`${API}/runs`, {
        task: task.trim(),
        mode,
        provider: modelMeta.provider,
        model: modelId,
        session_id: sid.current,
        team_id: teamId,
        upload_ids: attachments.map((a) => a.id),
        skill_id: skillId === "none" ? null : skillId,
      }, { withCredentials: true })
      .then((res) => {
        const r = res.data;
        setResult(r);
        setRunning(false);
        setAttachments([]);
        setJobs((prev) => [r, ...prev].slice(0, 8));
        toast({ title: "Run complete", description: `Auditor: ${r.verdict} · ${r.rows} rows read` });
      })
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        setRunning(false);
        setActiveStep(-1);
        setResult(null);
        if (status === 401) {
          toast({ title: "Please sign in", description: "Your session expired — sign in to run again." });
          navigate("/login");
        } else if (status === 429) {
          toast({ title: "Rate limit reached", description: detail || "You've hit the run limit. Try again shortly." });
        } else {
          toast({ title: "Run failed", description: typeof detail === "string" ? detail : "Something went wrong. Please try again." });
        }
      });
  };

  const reset = () => {
    timers.current.forEach(clearTimeout);
    setRunning(false);
    setActiveStep(-1);
    setResult(null);
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/runs`, { withCredentials: true });
    } catch {}
    setJobs([]);
  };

  const steps = result?.steps || [];
  const verdictColor = { VERIFIED: "bg-primary text-primary-foreground", PARTIAL: "bg-amber-500 text-white" }[result?.verdict] || "bg-muted text-foreground";

  return (
    <div className="min-h-screen bg-background">
      <Toaster />
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Cloud className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <span className="font-display text-xl font-bold tracking-tight">Nimbus</span>
            <Badge variant="secondary" className="ml-1 hidden sm:inline-flex">Workspace</Badge>
          </Link>
          <div className="flex items-center gap-3">
            <HelpButton />
            {user ? (
              <>
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=059669&color=fff`}
                  alt={user.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
                <span className="hidden max-w-[120px] truncate text-sm font-medium text-foreground sm:inline">{user.name}</span>
                <Button asChild variant="ghost" size="icon" className="text-muted-foreground" aria-label="Settings">
                  <Link to="/settings" data-testid="workspace-settings-link"><SettingsIcon className="h-4 w-4" /></Link>
                </Button>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={logout} aria-label="Log out">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button variant="outline" className="rounded-full border-border text-sm font-medium" onClick={() => navigate("/login")}>
                Sign in
              </Button>
            )}
            <Button asChild variant="ghost" className="text-sm font-medium text-muted-foreground">
              <Link to="/"><ArrowLeft className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">Back to site</span></Link>
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-5 py-8 sm:px-8 lg:grid-cols-[1fr_340px]">
        {/* main column */}
        <div className="space-y-6">
          <VerifyEmailBanner user={user} onToast={(m) => toast({ title: "Email confirmation", description: m })} />
          {/* composer */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h1 className="font-display text-lg font-semibold text-foreground">New agent run</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Attach CSV, Excel, JSON, PDF or text files and describe the goal. Nimbus runs real data tools on them, and an auditor checks every figure in the report against the tool results.
            </p>

            {!user && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/20 bg-accent px-4 py-3">
                <p className="text-sm text-accent-foreground">
                  Sign in to run the agent — your runs stay private to your account.
                </p>
                <Button size="sm" className="rounded-full bg-primary font-semibold" onClick={() => navigate("/login")}>
                  Sign in
                </Button>
              </div>
            )}

            <Textarea
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder="e.g. Reconcile Q3 vendor invoices against the ledger and flag mismatches"
              className="mt-4 min-h-[96px] resize-none border-border bg-background text-base"
              disabled={running}
            />

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <Cpu className="h-4 w-4" />
                </span>
                {fixedModel ? (
                  <span className="text-sm font-medium" data-testid="team-fixed-model">
                    {fixedModel.id === "gemini-2.5-flash-lite" ? "Gemini 2.5 Flash-Lite" : fixedModel.id}
                  </span>
                ) : (
                <Select value={modelId} onValueChange={setModelId} disabled={running}>
                  <SelectTrigger className="h-10 w-[220px] border-border bg-background">
                    <SelectValue placeholder="Choose a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {LLM_MODELS.map((g) => (
                      <SelectGroup key={g.provider}>
                        <SelectLabel>{g.label}</SelectLabel>
                        {g.models.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}{m.tag ? ` · ${m.tag}` : ""}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                )}
              </div>
              {fixedModel ? (
                <span className="text-xs text-muted-foreground" data-testid="team-model-note">Team runs use this model and each counts as 1 run</span>
              ) : modelMeta.runWeightComplex > 1 ? (
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400" data-testid="run-weight-note">
                  {runWeight > 1
                    ? `Opus runs count as ${runWeight} in Complex mode (this run uses ${runWeight} of your plan's runs)`
                    : `Opus runs count as ${modelMeta.runWeightComplex} in Complex mode; Fast mode counts as 1`}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Powered by your selected LLM</span>
              )}
            </div>

            {user && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <Users className="h-4 w-4" />
                  </span>
                  <Select value={scope} onValueChange={setScope} disabled={running}>
                    <SelectTrigger className="h-10 w-[200px] border-border bg-background">
                      <SelectValue placeholder="Workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">Personal (private)</SelectItem>
                      {teams.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Shared teams</SelectLabel>
                          {teams.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <input ref={fileRef} type="file" className="hidden" onChange={handleFile}
                       accept=".csv,.xls,.xlsx,.pdf,.txt,.json" />
                {attachments.map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm">
                    <FileText className="h-3.5 w-3.5 text-primary" />
                    <span className="max-w-[160px] truncate">{a.filename}</span>
                    <button onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} aria-label="Remove attachment">
                      <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                  </span>
                ))}
                {attachments.length < 5 && (
                  <Button variant="outline" size="sm" className="rounded-full border-border"
                          disabled={running || uploading} onClick={() => fileRef.current?.click()}>
                    {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Paperclip className="mr-1.5 h-4 w-4" />}
                    Attach file
                  </Button>
                )}
                <Select value={skillId} onValueChange={setSkillId} disabled={running}>
                  <SelectTrigger className="h-9 w-[180px] border-border bg-background text-sm">
                    <SelectValue placeholder="Skill" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No skill</SelectItem>
                    {skills.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="rounded-full text-xs" disabled={running} onClick={saveSkill}>
                  <Sparkles className="mr-1 h-3.5 w-3.5" /> Save as skill
                </Button>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-lg border border-border bg-background p-1">
                <button
                  onClick={() => setMode("fast")}
                  disabled={running}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    mode === "fast" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Zap className="h-3.5 w-3.5" /> Fast
                </button>
                <button
                  onClick={() => setMode("complex")}
                  disabled={running}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    mode === "complex" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Layers className="h-3.5 w-3.5" /> Complex{!fixedModel && modelMeta.runWeightComplex > 1 ? ` · counts as ${modelMeta.runWeightComplex}` : ""}
                </button>
              </div>

              <div className="flex gap-2">
                {(running || result) && (
                  <Button variant="outline" onClick={reset} className="rounded-full border-border">
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
                  </Button>
                )}
                <Button onClick={startRun} disabled={running} className="rounded-full bg-primary font-semibold">
                  {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                  {running ? "Running..." : "Run agent"}
                </Button>
              </div>
            </div>

            {!running && !result && (
              <div className="mt-5 flex flex-wrap gap-2">
                {SAMPLE_TASKS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setTask(s)}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* live run panel */}
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-5 py-3">
              <Monitor className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Agent steps</span>
              {running && (
                <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-dot" /> Working
                </span>
              )}
            </div>

            <div ref={logRef} className="max-h-[340px] min-h-[220px] space-y-2 overflow-y-auto p-5">
              {running ? (
                <div className="flex h-[180px] flex-col items-center justify-center text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <p className="mt-4 text-sm font-medium text-foreground">Working on your files…</p>
                  <p className="mt-1 text-sm text-muted-foreground">Each tool call and its result will be listed here when the run finishes.</p>
                </div>
              ) : !result ? (
                <div className="flex h-[180px] flex-col items-center justify-center text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent">
                    <Sparkles className="h-6 w-6 text-accent-foreground" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-foreground">Ready when you are</p>
                  <p className="mt-1 text-sm text-muted-foreground">Every tool the agent runs is logged here with its result.</p>
                </div>
              ) : steps.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tools ran: there was no data to work on, so the output below is an unverified AI draft.</p>
              ) : (
                steps.map((st) => (
                  <div key={st.n} className="flex items-start gap-3 rounded-xl border border-border bg-background px-4 py-3">
                    {st.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{st.n}. {st.tool}{st.why ? ` — ${st.why}` : ""}</p>
                      <p className="break-words text-xs text-muted-foreground">{st.summary}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {result && (
              <div className="border-t border-border p-5">
                <div className="flex items-center justify-between rounded-xl bg-accent px-4 py-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-accent-foreground" />
                    <span className="font-semibold text-accent-foreground">Auditor verdict</span>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${verdictColor}`}>
                    {result.verdict}
                  </span>
                </div>
                {result.audit?.note && <p className="mt-2 text-xs text-muted-foreground">{result.audit.note}</p>}
                {(result.audit?.flags || []).length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-xs text-amber-700">
                    {result.audit.flags.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                )}
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    { k: "Rows read", v: result.rows },
                    { k: "Figures flagged", v: result.flags },
                    { k: "Figures verified", v: result.audit?.figures_checked ? `${result.audit.figures_verified}/${result.audit.figures_checked}` : "—" },
                  ].map((m) => (
                    <div key={m.k} className="rounded-xl border border-border bg-background p-3 text-center">
                      <p className="font-display text-xl font-bold text-foreground">{m.v}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{m.k}</p>
                    </div>
                  ))}
                </div>

                {result.output && (
                  <div className="mt-4 rounded-xl border border-border bg-background p-4">
                    <div className="flex items-center gap-2 border-b border-border pb-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">Agent output{result.estimated ? " (estimated, not fully verified)" : ""}</span>
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">
                        <Cpu className="h-3 w-3" /> {result.model || modelMeta.name}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm leading-relaxed text-muted-foreground">
                      {renderOutput(result.output)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* sidebar: history */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-display text-sm font-semibold text-foreground">Recent jobs</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {user ? "Saved to your account" : "Sign in to keep a history"}
            </p>

            <div className="mt-4 space-y-3">
              {jobs.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
                  No runs yet
                </p>
              ) : (
                jobs.map((j) => (
                  <div key={j.id} className="rounded-xl border border-border bg-background p-3">
                    <p className="line-clamp-2 text-sm font-medium text-foreground">{j.task}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        {j.mode === "fast" ? <Zap className="h-3 w-3" /> : <Layers className="h-3 w-3" />}
                        {j.mode} · {j.time}
                      </span>
                      <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold text-accent-foreground">
                        {j.verdict}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {jobs.length > 0 && (
              <Button
                variant="ghost"
                className="mt-3 w-full text-xs text-muted-foreground hover:text-destructive"
                onClick={clearHistory}
              >
                Clear history
              </Button>
            )}
          </div>

          {user && (
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <h2 className="font-display text-sm font-semibold text-foreground">Shared workspaces</h2>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Create a team and invite members by email. They join only if they accept.</p>

              {myInv.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="workspace-my-invites">
                  <p className="text-xs font-semibold text-foreground">Invitations for you</p>
                  {myInv.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-2 rounded-xl border border-primary/30 bg-accent px-3 py-2">
                      <span className="truncate text-xs text-accent-foreground">{inv.team_name}{inv.inviter_name ? ` · ${inv.inviter_name}` : ""}</span>
                      <span className="flex shrink-0 gap-1">
                        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => answerInvite(inv, true)}>Accept</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => answerInvite(inv, false)}>Decline</Button>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)}
                       placeholder="New team name" className="h-9 text-sm"
                       onKeyDown={(e) => e.key === "Enter" && createTeam()} />
                <Button size="icon" className="h-9 w-9 shrink-0 rounded-lg bg-primary" disabled={teamBusy} onClick={createTeam} aria-label="Create team">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {teamId && (
                <div className="mt-4 rounded-xl border border-border bg-background p-3">
                  <p className="text-xs font-semibold text-foreground">
                    {teams.find((t) => t.id === teamId)?.name}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                           placeholder="invite@email.com" type="email" className="h-9 text-sm"
                           onKeyDown={(e) => e.key === "Enter" && inviteMember()} />
                    <Button size="icon" variant="outline" className="h-9 w-9 shrink-0 rounded-lg border-border" disabled={teamBusy} onClick={inviteMember} aria-label="Invite member">
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  </div>
                  {teamPending.length > 0 && (
                    <div className="mt-3 space-y-1" data-testid="workspace-team-pending">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Pending invitations</p>
                      {teamPending.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="truncate">{inv.email}</span>
                          <button type="button" className="shrink-0 underline hover:text-destructive" onClick={() => revoke(inv)}>Withdraw</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(teams.find((t) => t.id === teamId)?.member_details || []).map((m) => (
                      <span key={m.user_id} className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">
                        {m.name || m.email}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-primary/20 bg-accent p-5">
            <ShieldCheck className="h-6 w-6 text-accent-foreground" />
            <p className="mt-3 text-sm font-semibold text-accent-foreground">Agent output is AI-generated</p>
            <p className="mt-1 text-xs leading-relaxed text-accent-foreground/80">
              The model writes the report; the figures come from real tools run on your files. The
              auditor marks a run VERIFIED only when every figure traces to a tool result. Anything
              else is labelled PARTIAL or ESTIMATED.
            </p>
          </div>
        </aside>
      </div>
      <footer data-testid="workspace-legal-footer" className="mx-auto max-w-7xl px-5 pb-10 pt-4 sm:px-8 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-6">
          <span>© {new Date().getFullYear()} Nimbus · Headkey Software Technologies Private Limited (CIN U72900KA2019PTC130529)</span>
          <LegalFooterLinks />
          <a href="mailto:info@headkeysoftwaresolution.com" className="underline">Grievance Officer: Suhas Raju, info@headkeysoftwaresolution.com</a>
          <Link to="/settings" className="underline">Settings</Link>
        </div>
      </footer>
    </div>
  );
};

export default Workspace;
